import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';

/**
 * Write feedback for the whole app. Mutations are fire-and-forget from the user's
 * point of view, so every one of them needs somewhere to say "saved" or "that
 * failed and here's why" — a shared stack beats an error banner per screen.
 */
const ToastContext = createContext(() => {});

/** `toast('Lead saved')` or `toast(message, 'error')`. Returns nothing. */
export const useToast = () => useContext(ToastContext);

const TONES = {
  success: { bg: '#e6f7f2', border: '#0d9488', ink: '#0f5c53', icon: '✓' },
  error: { bg: '#fdeaea', border: 'var(--crit)', ink: '#8a1c1c', icon: '✕' },
  info: { bg: 'var(--card-2)', border: 'var(--border)', ink: 'var(--ink)', icon: 'ℹ' },
};

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback((id) => setItems((list) => list.filter((t) => t.id !== id)), []);

  const push = useCallback(
    (message, tone = 'success') => {
      if (!message) return;
      const id = ++seq.current;
      setItems((list) => [...list, { id, message, tone }]);
      // errors deserve longer on screen — they usually carry something to act on
      setTimeout(() => dismiss(id), tone === 'error' ? 6000 : 3200);
    },
    [dismiss]
  );

  // `push` is stable, so consumers can depend on the toaster without re-rendering.
  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Box
        aria-live="polite"
        sx={{
          position: 'fixed',
          right: { xs: 12, sm: 20 },
          bottom: { xs: 12, sm: 20 },
          left: { xs: 12, sm: 'auto' },
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pointerEvents: 'none',
        }}
      >
        {items.map((t) => {
          const tone = TONES[t.tone] || TONES.info;
          return (
            <Box
              key={t.id}
              onClick={() => dismiss(t.id)}
              sx={{
                pointerEvents: 'auto',
                cursor: 'pointer',
                maxWidth: { xs: '100%', sm: 380 },
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: '11px 14px',
                borderRadius: '12px',
                border: `1px solid ${tone.border}`,
                borderLeft: `4px solid ${tone.border}`,
                background: tone.bg,
                color: tone.ink,
                boxShadow: 'var(--shadow)',
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>{tone.icon}</Box>
              <Box sx={{ minWidth: 0, wordBreak: 'break-word' }}>{t.message}</Box>
            </Box>
          );
        })}
      </Box>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
